import * as Popover from '@radix-ui/react-popover'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'


import { cn } from '@shared/lib/cn'
import { SortDirection } from '@shared/types'

import styles from './styles.module.scss'


interface SelectSortProps<T extends string> {
  fields: Record<
    T,
    {
      value: string;
      direction: SortDirection;
      fieldName: string;
    }
  >;
  value?: T;
  onChange: (fieldName: string) => void;
  className?: string;
}

export default function SelectSort<T extends string> ({
  fields,
  value,
  className,
  onChange,
}: SelectSortProps<T>) {
  const { t: ts } = useTranslation('sort')

  const entries = Object.entries(fields) as [
    T,
    { value: string; direction: -1 | 1 },
  ][]
  const [ open, setOpen ] = useState(false)
  const [ selected, setSelected ] = useState<T>(entries[0][0])

  useEffect(() => {
    if (value) {
      setSelected(value)
    }
  }, [ value ])

  const handleSelect = (key: T) => {
    setSelected(key)
    onChange(key)
    setOpen(false)
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button className={cn('reset-button', styles.trigger, className)}>
          {ts(fields[selected]?.value)}

          <ReactSVG
            src="/icons/arrow.svg"
            wrapper="span"
            className={styles.trigger__icon}
          />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={styles.popover}
          sideOffset={18}
          side="bottom"
          align="end"
        >
          <div className={styles.content}>
            {entries.map(([ key, { value } ]) => (
              <button
                className={cn('reset-button', styles.content__item, {
                  [styles.active]: selected == key,
                })}
                key={key}
                onClick={() => handleSelect(key)}
              >
                {ts(value)}
              </button>
            ))}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
